import React from "react";
import type {
  CanonicalCommentWorkspaceMode,
  CanonicalCommentWorkspaceSurface,
  DiscussionModel,
} from "../model/types";
import styles from "../CanonicalCommentWorkspace.module.css";

type WorkspaceTopBarProps = {
  mode: CanonicalCommentWorkspaceMode;
  model: DiscussionModel;
  surface?: CanonicalCommentWorkspaceSurface;
  onModeChange: (mode: CanonicalCommentWorkspaceMode) => void;
};

const MODES: { value: CanonicalCommentWorkspaceMode; label: string; hint: string }[] = [
  {
    value: "branch",
    label: "Branch",
    hint: "Best default: selected comment, parent path, siblings, and direct replies.",
  },
  {
    value: "overview",
    label: "Overview",
    hint: "Use this to scan every top-level thread in a familiar nested layout.",
  },
  {
    value: "lanes",
    label: "Lanes",
    hint: "Use columns when you need to inspect deep reply chains level by level.",
  },
  {
    value: "map",
    label: "Map",
    hint: "Use the map to understand structure, density, and the shape of the discussion.",
  },
  {
    value: "recent",
    label: "Recent",
    hint: "Use recent activity when you only need to see what changed lately.",
  },
];

export const WorkspaceTopBar: React.FC<WorkspaceTopBarProps> = ({
  mode,
  model,
  surface = "docked",
  onModeChange,
}) => {
  const activeMode = MODES.find((item) => item.value === mode) || MODES[0];
  const isExpanded = surface === "expanded";

  return (
    <div className={`${styles.workspaceTopBar} ${isExpanded ? styles.workspaceTopBarExpanded : ""}`}>
      <nav className={styles.workspaceModeTabs} aria-label="Comment workspace modes">
        {MODES.map((item) => (
          <button
            key={item.value}
            type="button"
            className={mode === item.value ? styles.workspaceModeTabActive : ""}
            title={item.hint}
            onClick={() => onModeChange(item.value)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {isExpanded && (
        <div className={styles.workspaceModeHint} aria-live="polite">
          <strong>{activeMode.label}</strong>
          <span>{activeMode.hint}</span>
        </div>
      )}

      <div className={styles.workspaceCounts} aria-label="Discussion summary">
        <span>{model.roots.length} threads</span>
        <span>{model.all.length} comments</span>
        <span>{model.anchoredRootCount} anchored</span>
        <span>{model.generalRootCount} general</span>
      </div>
    </div>
  );
};
